import { useState, useEffect } from "react";
import { FileXls, DownloadSimple, UsersThree, GraduationCap, UserCircle, SignIn, Broom } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const DAY_ABBR = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Ahd"];
const mondayOf = (dateStr) => { const d = new Date(dateStr); const off = (d.getDay() + 6) % 7; d.setDate(d.getDate() - off); return d.toISOString().slice(0, 10); };

export default function ManageExport() {
  const [busy, setBusy] = useState(false);
  const [gurus, setGurus] = useState([]);
  const [tgId, setTgId] = useState("");
  const [tgDate, setTgDate] = useState("");
  const today = new Date();
  const [tgMonth, setTgMonth] = useState(String(today.getMonth() + 1));
  const [tgYear, setTgYear] = useState(String(today.getFullYear()));

  // parent login report (weekly)
  const [plWeek, setPlWeek] = useState(mondayOf(new Date().toISOString().slice(0, 10)));
  const [plReport, setPlReport] = useState(null);
  const [plBusy, setPlBusy] = useState(false);

  const loadPlReport = async () => {
    setPlBusy(true);
    try {
      const r = await api.get(`/admin/parent-login-report?week_start=${mondayOf(plWeek)}`);
      setPlReport(r.data);
    } catch (e) { toast.error("Gagal memuat laporan"); } finally { setPlBusy(false); }
  };

  // orphan parent cleanup
  const [orphans, setOrphans] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const loadOrphans = async () => {
    setOrphanBusy(true);
    try { const r = await api.get("/admin/orphan-parents"); setOrphans(r.data); }
    catch (e) { toast.error("Gagal memuat"); } finally { setOrphanBusy(false); }
  };
  const cleanupOrphans = async () => {
    if (!window.confirm(`Hapus ${orphans?.count || 0} akun orang tua yatim (tidak tertaut ke anak)? Tindakan ini tidak bisa dibatalkan.`)) return;
    setOrphanBusy(true);
    try {
      const r = await api.post("/admin/orphan-parents/cleanup");
      toast.success(`${r.data.deleted} akun yatim dihapus`);
      await loadOrphans();
    } catch (e) { toast.error("Gagal menghapus"); } finally { setOrphanBusy(false); }
  };

  useEffect(() => { api.get("/users?role=guru").then((r) => setGurus(r.data)).catch(() => {}); }, []);

  const dl = async (path, fname) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("File diunduh");
    } catch (e) { toast.error("Gagal mengunduh"); }
  };

  const run = async () => {
    setBusy(true);
    try {
      // 1) download the xlsx file
      const res = await api.get("/admin/export.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Data_TK_Muslim_Kids_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("File Excel diunduh");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Gagal mengekspor data");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Ekspor Data" />
      <div className="px-5 pt-6 pb-10">
        <div className="bg-gradient-to-br from-green-700 to-emerald-500 text-white rounded-3xl p-6 shadow-lg relative overflow-hidden mb-5">
          <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full" />
          <FileXls size={40} weight="fill" className="text-white mb-2" />
          <h2 className="font-heading font-bold text-xl leading-tight">Ekspor Data ke Excel</h2>
          <p className="text-white/85 text-sm mt-1">Unduh seluruh data siswa, guru & orang tua dalam file Excel.</p>
        </div>

        <p className="font-heading font-semibold text-stone-800 mb-2 text-sm">Yang diekspor</p>
        <div className="space-y-2 mb-6">
          <Row icon={UsersThree} title="Data Siswa" desc="Nama, NIS, kelas, guru, jenis kelamin, TTL, alamat" tone="text-sky-600" bg="bg-sky-100" />
          <Row icon={GraduationCap} title="Data Guru" desc="Nama, kode, kelas, nomor HP, email" tone="text-primary" bg="bg-primary/10" />
          <Row icon={UserCircle} title="Data Orang Tua" desc="Nama, nomor HP, email, nama anak" tone="text-fuchsia-600" bg="bg-fuchsia-100" />
        </div>

        <button data-testid="export-send-btn" onClick={run} disabled={busy}
          className="w-full bg-primary text-white font-semibold py-4 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
          {busy ? "Memproses..." : <><DownloadSimple size={20} weight="bold" /> Export Excel</>}
        </button>
        <p className="text-stone-400 text-xs text-center mt-3">File .xlsx akan terunduh otomatis ke perangkat Anda.</p>

        <div className="mt-6 border-t border-stone-200/70 pt-5">
          <p className="font-heading font-semibold text-stone-800 mb-2 text-sm">Ekspor Laporan</p>
          <button data-testid="export-daily-btn" onClick={() => dl("/admin/export/daily.xlsx", "Laporan_Harian.xlsx")}
            className="w-full mb-2 border-2 border-primary text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <DownloadSimple size={18} weight="bold" /> Export Laporan Harian Siswa
          </button>
          <button data-testid="export-teacher-att-btn" onClick={() => dl("/admin/export/teacher-attendance.xlsx", "Kehadiran_Guru.xlsx")}
            className="w-full border-2 border-primary text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <DownloadSimple size={18} weight="bold" /> Export Kehadiran Guru
          </button>
        </div>

        <div className="mt-6 border-t border-stone-200/70 pt-5">
          <p className="font-heading font-semibold text-stone-800 mb-1 text-sm">Export Harian per Guru</p>
          <p className="text-stone-400 text-xs mb-3">Satu file berisi laporan tiap murid guru (1 sheet per murid): hafalan, kegiatan, catatan guru, evaluasi lancar & ulang.</p>
          <label className="text-xs text-stone-500">Pilih Guru</label>
          <select data-testid="tg-guru-select" value={tgId} onChange={(e) => setTgId(e.target.value)}
            className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-3 mt-1 outline-none focus:border-primary text-sm">
            <option value="">— Pilih guru —</option>
            {gurus.map((g) => <option key={g.id} value={g.id}>{g.name}{g.kelas ? ` — ${g.kelas}` : ""}</option>)}
          </select>
          <label className="text-xs text-stone-500">Tanggal (opsional, kosongkan untuk per bulan)</label>
          <input type="date" data-testid="tg-date" value={tgDate} onChange={(e) => setTgDate(e.target.value)}
            className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-3 mt-1 outline-none focus:border-primary text-sm" />
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="text-xs text-stone-500">Bulan</label>
              <select data-testid="tg-month" value={tgMonth} onChange={(e) => setTgMonth(e.target.value)}
                className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mt-1 outline-none focus:border-primary text-sm">
                {["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"].map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-stone-500">Tahun</label>
              <select data-testid="tg-year" value={tgYear} onChange={(e) => setTgYear(e.target.value)}
                className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mt-1 outline-none focus:border-primary text-sm">
                {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <button data-testid="export-teacher-daily-btn" disabled={!tgId}
            onClick={() => {
              const g = gurus.find((x) => x.id === tgId);
              const qs = tgDate ? `date=${tgDate}` : `month=${tgMonth}&year=${tgYear}`;
              dl(`/admin/export/teacher-daily.xlsx?teacher_id=${tgId}&${qs}`, `Laporan_${(g?.name || "Guru").replace(/\s+/g, "_")}.xlsx`);
            }}
            className="w-full bg-primary text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50">
            <DownloadSimple size={18} weight="bold" /> Export Laporan Guru (per Murid)
          </button>
        </div>

        <div className="mt-6 border-t border-stone-200/70 pt-5">
          <div className="flex items-center gap-2 mb-1">
            <SignIn size={18} weight="duotone" className="text-primary" />
            <p className="font-heading font-semibold text-stone-800 text-sm">Laporan Login Orang Tua (per Minggu)</p>
          </div>
          <p className="text-stone-400 text-xs mb-3">Tabel per kelompok kelas. Angka = jumlah login orang tua pada hari itu. Diurutkan dari yang paling sering login.</p>
          <label className="text-xs text-stone-500">Pilih minggu (tanggal mana saja dalam minggu tsb)</label>
          <input type="date" data-testid="pl-week" value={plWeek} onChange={(e) => setPlWeek(e.target.value)}
            className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-3 mt-1 outline-none focus:border-primary text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="pl-view-btn" onClick={loadPlReport} disabled={plBusy}
              className="border-2 border-primary text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50">
              {plBusy ? "Memuat..." : "Lihat Tabel"}
            </button>
            <button data-testid="pl-export-btn" onClick={() => dl(`/admin/export/parent-login.xlsx?week_start=${mondayOf(plWeek)}`, `Login_OrangTua_${mondayOf(plWeek)}.xlsx`)}
              className="bg-primary text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <DownloadSimple size={18} weight="bold" /> Excel
            </button>
          </div>

          {plReport && (
            <div className="mt-4 space-y-5" data-testid="pl-report">
              <p className="text-xs text-stone-500">Minggu: {plReport.week_start} s/d {plReport.week_end}</p>
              {plReport.groups.map((grp) => (
                <div key={grp.group} data-testid={`pl-group-${grp.group}`}>
                  <p className="font-heading font-bold text-stone-800 text-sm mb-2">{grp.group}</p>
                  <div className="overflow-x-auto rounded-2xl border border-stone-200/60 bg-white shadow-sm">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-primary text-white">
                          <th className="py-2 px-2 text-left font-semibold">No</th>
                          <th className="py-2 px-2 text-left font-semibold whitespace-nowrap">Nama Orang Tua</th>
                          {plReport.days.map((d, i) => (
                            <th key={d} className="py-2 px-2 text-center font-semibold whitespace-nowrap">{DAY_ABBR[i]}<br />{parseInt(d.slice(8, 10), 10)}</th>
                          ))}
                          <th className="py-2 px-2 text-center font-semibold">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grp.rows.length === 0 && (
                          <tr><td colSpan={10} className="py-3 px-2 text-center text-stone-400">Belum ada data.</td></tr>
                        )}
                        {grp.rows.map((row, i) => (
                          <tr key={i} className="border-t border-stone-100">
                            <td className="py-2 px-2 text-stone-500">{i + 1}</td>
                            <td className="py-2 px-2 text-stone-800 font-medium whitespace-nowrap">{row.name}</td>
                            {row.days.map((n, di) => (
                              <td key={di} className={`py-2 px-2 text-center ${n ? "text-primary font-bold" : "text-stone-300"}`}>{n || "-"}</td>
                            ))}
                            <td className="py-2 px-2 text-center font-bold text-stone-700">{row.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 border-t border-stone-200/70 pt-5">
          <div className="flex items-center gap-2 mb-1">
            <Broom size={18} weight="duotone" className="text-primary" />
            <p className="font-heading font-semibold text-stone-800 text-sm">Rapikan Akun Orang Tua</p>
          </div>
          <p className="text-stone-400 text-xs mb-3">Hapus akun orang tua "yatim" yang tidak tertaut ke anak mana pun (mis. akun uji/duplikat lama). Akun yang punya anak tidak akan tersentuh.</p>
          <button data-testid="orphan-preview-btn" onClick={loadOrphans} disabled={orphanBusy}
            className="w-full border-2 border-primary text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50 mb-3">
            {orphanBusy ? "Memuat..." : "Pratinjau Akun Yatim"}
          </button>
          {orphans && (
            <div data-testid="orphan-result">
              <p className="text-sm font-semibold text-stone-700 mb-2">Ditemukan <span className="text-red-600" data-testid="orphan-count">{orphans.count}</span> akun yatim</p>
              {orphans.count > 0 && (
                <>
                  <div className="max-h-52 overflow-y-auto rounded-2xl border border-stone-200/60 bg-white divide-y divide-stone-100 mb-3">
                    {orphans.items.map((o) => (
                      <div key={o.id} className="px-3 py-2 flex items-center justify-between gap-2">
                        <span className="text-sm text-stone-700 truncate">{o.name || "(tanpa nama)"}</span>
                        <span className="text-xs text-stone-400 shrink-0">{o.username || o.phone || "-"}</span>
                      </div>
                    ))}
                  </div>
                  <button data-testid="orphan-cleanup-btn" onClick={cleanupOrphans} disabled={orphanBusy}
                    className="w-full bg-red-500 text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50">
                    <Broom size={18} weight="bold" /> Hapus {orphans.count} Akun Yatim
                  </button>
                </>
              )}
              {orphans.count === 0 && <p className="text-stone-400 text-sm">Tidak ada akun yatim. Data sudah rapi. 🎉</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, title, desc, tone, bg }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center gap-3 shadow-sm">
      <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center shrink-0`}><Icon size={22} weight="duotone" className={tone} /></div>
      <div className="min-w-0"><p className="font-semibold text-stone-800 text-sm">{title}</p><p className="text-stone-400 text-xs">{desc}</p></div>
    </div>
  );
}
